import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 as LoaderIcon } from 'lucide-react';

import { classNames } from '@m1work/shared-utils';

import styles from './Button.module.scss';

export type ButtonVariant = 'fill' | 'outlined' | 'ghost';
export type ButtonColor = 'primary' | 'accent' | 'error' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** Filled, outlined or text-only. @default 'fill' */
    variant?: ButtonVariant;
    /** Colour role. @default 'primary' */
    color?: ButtonColor;
    /** @default 'md' */
    size?: ButtonSize;
    /** Pill-shaped corners. @default true */
    rounded?: boolean;
    /** Pressed / active look, e.g. the current filter. @default false */
    selected?: boolean;
    /** Render the child element (e.g. the app's `<Link>`) with the button's styles instead of a `<button>`. @default false */
    asChild?: boolean;
    /** Stretch to the full width of the parent. @default false */
    isFullWidth?: boolean;
    /** Let a long label wrap onto more lines (narrow screens); the button grows in height. @default false */
    isMultiline?: boolean;
    /** Icon before the text; ignored with `asChild`. */
    iconLeft?: ReactNode;
    /** Icon after the text; ignored with `asChild`. */
    iconRight?: ReactNode;
    /** An action is running: a spinner takes the left icon's place and clicks are blocked. Ignored with `asChild`. @default false */
    isLoading?: boolean;
}

/**
 * The main action button. For a link that looks like a button, pass the app's link with `asChild`.
 *
 * @example
 * <Button variant="outlined" iconLeft={<PhoneIcon />} onClick={openQuickOrder}>
 *     {t('product.quick-order')}
 * </Button>
 * <Button asChild>
 *     <Link href="/cart">{t('cart.title')}</Link>
 * </Button>
 */
export function Button({
    variant = 'fill',
    color = 'primary',
    size = 'md',
    rounded = true,
    selected = false,
    asChild = false,
    isFullWidth = false,
    isMultiline = false,
    iconLeft,
    iconRight,
    isLoading = false,
    className,
    children,
    disabled,
    ...props
}: ButtonProps): ReactElement {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            className={classNames(
                styles.button,
                styles[`variant-${variant}`],
                styles[`color-${color}`],
                styles[`size-${size}`],
                rounded && styles.rounded,
                isFullWidth && styles['full-width'],
                isMultiline && styles.multiline,
                selected && styles.selected,
                isLoading && styles['is-loading'],
                className,
            )}
            disabled={asChild ? disabled : disabled || isLoading}
            aria-busy={isLoading || undefined}
            {...props}
        >
            {asChild ? (
                children
            ) : (
                <>
                    {isLoading ? (
                        <span className={styles.icon}>
                            <LoaderIcon className={styles.spinner} aria-hidden />
                        </span>
                    ) : (
                        iconLeft && <span className={styles.icon}>{iconLeft}</span>
                    )}
                    {children}
                    {iconRight && <span className={styles.icon}>{iconRight}</span>}
                </>
            )}
        </Comp>
    );
}
